import React, {
	createContext,
	useContext,
	useState,
	useCallback,
	useMemo,
} from 'react';
import { getSettings } from '@wordpress/date';
import { format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

type TimezoneMode = 'site' | 'local';

interface TimezoneContextValue {
	mode: TimezoneMode;
	siteTimezone: string;
	localTimezone: string;
	siteTimezoneLabel: string;
	localTimezoneLabel: string;
	siteTimezoneOffset: number;
	localTimezoneOffset: number;
	setMode: (mode: TimezoneMode) => void;
	// Helper functions for date conversion
	convertToSiteTimezone: (date: Date) => Date;
	convertToLocalTimezone: (date: Date) => Date;
	formatInSiteTimezone: (date: Date, formatStr: string) => string;
}

const TimezoneContext = createContext<TimezoneContextValue | null>(null);

interface TimezoneProviderProps {
	children: React.ReactNode;
	defaultMode?: TimezoneMode;
	useLocalStorage?: boolean;
	storageKey?: string;
}

export const TimezoneProvider: React.FC<TimezoneProviderProps> = ({
	children,
	defaultMode = 'site',
	useLocalStorage = false,
	storageKey = 'gpb-timezone-mode',
}) => {
	// Get timezone information
	const {
		siteTimezone,
		localTimezone,
		siteTimezoneLabel,
		localTimezoneLabel,
		siteTimezoneOffset,
		localTimezoneOffset,
	} = useMemo(() => {
		const wpSettings = getSettings();
		const siteTimezone = wpSettings.timezone.string || 'UTC';
		const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

		// Create friendly labels for timezones
		const siteTimezoneLabel =
			siteTimezone === 'UTC'
				? 'UTC'
				: siteTimezone.split('/').pop()?.replace(/_/g, ' ') ||
					siteTimezone;
		const localTimezoneLabel =
			localTimezone.split('/').pop()?.replace(/_/g, ' ') || localTimezone;

		// Calculate timezone offsets (in minutes)
		// We use a reference date to ensure consistent calculations
		const referenceDate = new Date();

		// Create a function to get a standardized offset value
		// We'll use a more direct approach to get the actual UTC offset
		const getTimezoneOffsetInMinutes = (timezone: string): number => {
			// Create a date in the specified timezone
			const date = new Date(
				new Date(referenceDate).toLocaleString('en-US', {
					timeZone: timezone,
				})
			);

			// Calculate the offset between this date and the UTC date
			// We'll use the UTC timestamp difference divided by 60000 (ms in a minute)
			const utcDate = new Date(
				referenceDate.getUTCFullYear(),
				referenceDate.getUTCMonth(),
				referenceDate.getUTCDate(),
				referenceDate.getUTCHours(),
				referenceDate.getUTCMinutes(),
				referenceDate.getUTCSeconds()
			);

			// Positive means ahead of UTC, negative means behind UTC
			return Math.round((date.getTime() - utcDate.getTime()) / 60000);
		};

		const siteTimezoneOffset = getTimezoneOffsetInMinutes(siteTimezone);
		const localTimezoneOffset = getTimezoneOffsetInMinutes(localTimezone);

		return {
			siteTimezone,
			localTimezone,
			siteTimezoneLabel,
			localTimezoneLabel,
			siteTimezoneOffset,
			localTimezoneOffset,
		};
	}, []);

	// Default to site timezone, but use local storage to persist user preference if enabled
	const [mode, setModeState] = useState<TimezoneMode>(() => {
		if (useLocalStorage) {
			const stored = localStorage.getItem(storageKey);
			return stored === 'local' || stored === 'site'
				? stored
				: defaultMode;
		}
		return defaultMode;
	});

	const setMode = useCallback(
		(newMode: TimezoneMode) => {
			setModeState(newMode);
			if (useLocalStorage) {
				localStorage.setItem(storageKey, newMode);
			}
		},
		[useLocalStorage, storageKey]
	);

	// Add conversion helpers
	const convertToSiteTimezone = useCallback(
		(date: Date): Date => {
			if (siteTimezone === localTimezone) {
				return date;
			}

			// Always convert to site timezone regardless of mode
			// This ensures dates are consistently interpreted in the site timezone
			// which is critical for booking dates
			const isoString = date.toISOString();
			const zonedDate = parseISO(isoString);
			return toZonedTime(zonedDate, siteTimezone);
		},
		[siteTimezone, localTimezone]
	);

	const convertToLocalTimezone = useCallback(
		(date: Date): Date => {
			if (mode === 'local' || siteTimezone === localTimezone) {
				return date;
			}

			// Convert from site timezone to local timezone
			const isoString = date.toISOString();
			const zonedDate = parseISO(isoString);
			return toZonedTime(zonedDate, localTimezone);
		},
		[mode, siteTimezone, localTimezone]
	);

	const formatInSiteTimezone = useCallback(
		(date: Date, formatStr: string): string => {
			const siteDate = convertToSiteTimezone(date);
			return format(siteDate, formatStr);
		},
		[convertToSiteTimezone]
	);

	const value = useMemo(
		() => ({
			mode,
			siteTimezone,
			localTimezone,
			siteTimezoneLabel,
			localTimezoneLabel,
			siteTimezoneOffset,
			localTimezoneOffset,
			setMode,
			convertToSiteTimezone,
			convertToLocalTimezone,
			formatInSiteTimezone,
		}),
		[
			mode,
			siteTimezone,
			localTimezone,
			siteTimezoneLabel,
			localTimezoneLabel,
			siteTimezoneOffset,
			localTimezoneOffset,
			setMode,
			convertToSiteTimezone,
			convertToLocalTimezone,
			formatInSiteTimezone,
		]
	);

	return (
		<TimezoneContext.Provider value={value}>
			{children}
		</TimezoneContext.Provider>
	);
};

export const useTimezone = (): TimezoneContextValue => {
	const context = useContext(TimezoneContext);
	if (!context) {
		throw new Error('useTimezone must be used within a TimezoneProvider');
	}
	return context;
};
